import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import { PreSaleLotteryApplicantsQuery } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  LotteryApplicationCard,
  LotteryApplicationCardSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  lotteryApplications:
    | PreSaleLotteryApplicantsQuery['preSaleLotteryApplicants']
    | undefined
  loading: boolean
  filter: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFilter: (filter: string | undefined) => void
  onFetchMore: (filter: string | undefined) => Promise<void>
}

export const LotteryApplicationsTemplate: Template<State, Action> = ({
  state,
  onFilter,
  onFetchMore,
}) => {
  const { t } = useTranslation('lotteryApplications')
  const { routerNames, routerPages } = useRouter()

  return (
    <BaseLayout
      header={{
        left: (
          <IconButton.Back
            edge="start"
            size="medium"
            href={routerPages.Management}
          />
        ),
        title: t('title'),
      }}
    >
      <Head title={[routerNames.LotteryApplications]} />
      <FilterMenu filter={state.filter} onFilter={onFilter} />
      <Box sx={{ px: 5, pb: 5 }}>
        <ScrollFetcher
          nodes={state.lotteryApplications?.edges?.map(({ node }) => (
            <LotteryApplicationCard key={node.id} lotteryApplication={node} />
          ))}
          loading={state.loading}
          hasNextPage={
            state.lotteryApplications?.pageInfo?.hasNextPage ?? false
          }
          onFetchMore={() => onFetchMore(state.filter)}
          emptyMessage={t('emptyMessage')}
          loadingComponent={
            <Stack spacing={4}>
              {Array.from(Array(5).keys()).map(index => (
                <LotteryApplicationCardSkeleton key={index} />
              ))}
            </Stack>
          }
        />
      </Box>
    </BaseLayout>
  )
}

const FilterMenu = ({
  filter,
  onFilter,
}: {
  filter: string
  onFilter: (filterType: string | undefined) => void
}) => {
  const { t } = useTranslation('lotteryApplications')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const options = useMemo(() => {
    return [
      { label: t('general'), value: undefined },
      { label: t('10_win'), value: '10_win' },
      { label: t('11_win_paid'), value: '11_win_paid' },
      { label: t('20_waiting_payment'), value: '20_waiting_payment' },
      { label: t('30_waiting_lottery'), value: '30_waiting_lottery' },
      { label: t('40_lose'), value: '40_lose' },
      { label: t('50_invalid'), value: '50_invalid' },
    ]
  }, [])
  const index =
    filter === 'general'
      ? 0
      : options.findIndex(option => option.value === filter) !== -1
      ? options.findIndex(option => option.value === filter)
      : 0
  const [selectedIndex, setSelectedIndex] = useState<number>(index)

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = useCallback(
    async (_: MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
      await onFilter(options[index]?.value)
    },
    [onFilter, options]
  )

  useEffect(() => {
    if (selectedIndex) {
      const UrlParams = `?filter=${options[selectedIndex].value}`
      // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
      window.history.replaceState(null, '', UrlParams)
    } else {
      const UrlParams = location.pathname
      window.history.replaceState(null, '', UrlParams)
    }
  }, [selectedIndex])

  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack alignItems="flex-end" sx={{ pb: 2 }}>
        <Button
          variant="text"
          sx={{ width: 'fit-content', mr: -2 }}
          onClick={handleClick}
        >
          <Typography variant="caption" sx={{ pr: 1 }}>
            {options[selectedIndex]?.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {options.map((option, index) => (
          <MenuItem
            sx={{ bgcolor: 'grey.100', minWidth: 200 }}
            key={index}
            selected={index === selectedIndex}
            onClick={event => handleMenuItemClick(event, index)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
